/**
 * The between-rounds summary.
 *
 * Two facts, both taken from what the game declared rather than from anything
 * this file knows about cards: who won the round, and where everyone stands.
 *
 * `winner` is a light convention across the platform - a game's game-over value
 * carries the winning seat as a string when there is one. Both shipped games do
 * it, and a game that does not simply shows no winner line rather than breaking.
 */

import type { MatchView } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';
import { sidesOf, sideOfSeat, sideName } from '../render/teams.js';

function winnerSeat(result: unknown): number | null {
  if (typeof result !== 'object' || result === null) return null;
  const value = (result as { winner?: unknown }).winner;
  const seat = typeof value === 'string' ? parseInt(value, 10) : value;
  return typeof seat === 'number' && !Number.isNaN(seat) ? seat : null;
}

export function Scoreboard({
  view,
  layout,
  compact,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  compact?: boolean | undefined;
}) {
  const won = winnerSeat(view.lastResult);
  const nameOf = (seat: number): string =>
    view.seats.find((info) => info.seat === seat)?.displayName ?? `Seat ${String(seat + 1)}`;

  /*
   * A partnership game is scored by SIDE, and this was showing nothing at all.
   *
   * Spades declares `teams` and no `seatScore`, because a seat's own score is
   * not a thing that exists there - so the per-seat lookup below found no
   * field, and the round-over panel rendered a title, a waiting line, and no
   * numbers whatsoever. The same declaration the table and the standings
   * already read fixes it here.
   */
  const sides = sidesOf(view, layout);
  const wonSide = sides && won !== null ? sideOfSeat(sides, won) : null;

  const field = layout.seatScore?.field;
  const raw = field ? view.view[field] : undefined;
  const scores = Array.isArray(raw) ? (raw as unknown[]) : null;

  const lowIsBetter = sides ? layout.teams?.lowIsBetter : layout.seatScore?.lowIsBetter;

  /*
   * One list, whichever the game is played by. Sorted, because a scoreboard
   * that is not sorted is a table.
   */
  const rows: { key: string; name: string; score: number | null; winner: boolean }[] = sides
    ? sides.map((side, index) => ({
        key: `side-${String(index)}`,
        name: sideName(side, nameOf),
        score: side.score,
        winner: index === wonSide,
      }))
    : view.seats.map((seat) => ({
        key: `seat-${String(seat.seat)}`,
        name: seat.displayName,
        score: typeof scores?.[seat.seat] === 'number' ? (scores[seat.seat] as number) : null,
        winner: seat.seat === won,
      }));

  rows.sort((a, b) => {
    if (a.score === null || b.score === null) return 0;
    return lowIsBetter ? a.score - b.score : b.score - a.score;
  });

  const winnerName =
    wonSide !== null && sides ? sideName(sides[wonSide]!, nameOf) : won === null ? null : nameOf(won);

  return (
    <div className={`scoreboard${compact ? ' compact' : ''}`}>
      {/*
        The moment somebody actually wins.
        
        This said "Ann wins round 7", which is true and reads as though a hand
        had ended. A room only reaches this state when the engine sets
        `gameover` - for Hearts that is somebody passing the target score, so
        what just finished is the whole game, and the seventh of them is not
        what anybody wants to hear about at that moment.

        The count moves below and gets the word that matches what it counts:
        these are games, however the protocol names them internally.
      */}
      <p className="scoreboard-title">
        {winnerName ? `${winnerName} wins` : 'Nobody wins this one'}
      </p>
      {view.round > 0 ? (
        <p className="scoreboard-round">Game {view.round} at this table</p>
      ) : null}

      {scores || sides ? (
        <ol className="scoreboard-rows">
          {rows.map((row) => (
            <li key={row.key} className={row.winner ? 'winner' : undefined}>
              <span className="scoreboard-name">{row.name}</span>
              <span className="scoreboard-score">{row.score ?? '—'}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {/*
        Nothing for the host, who is not waiting on anybody.
        
        They are the one everybody else is waiting on, and the button that ends
        the wait is on this same screen - telling them to wait for themselves
        read as the room being stuck. Worse in a patience, where the host is
        the only person at the table.
      */}
      {view.autoContinue ? (
        <p className="field-help">Next round starting…</p>
      ) : view.isHost ? null : (
        <p className="field-help">Waiting for the host to deal again.</p>
      )}
    </div>
  );
}
